import { useEffect, useRef, useState } from 'react'
import Icon from './Icon.jsx'

/**
 * A small rich-text box for the notes that go out on the schedule email.
 *
 * pell, at about three kilobytes, rather than a full editor framework. What these fields
 * hold is a couple of sentences and the odd bulleted list — the difference between that and
 * a plain textarea is bold, a list, a link and a bit of colour, and paying ninety kilobytes
 * for the rest of an editor to get them would be a poor trade on a dialog most people open,
 * read and close.
 *
 * Its stylesheet is deliberately not imported; the toolbar and the box are styled against
 * this app's own tokens in styles.css, the same arrangement the date picker uses.
 *
 * The HTML this produces is NOT trusted. It is typed by a person here, but it arrives at the
 * server as an ordinary JSON string like any other, and it ends up inside an email sent to
 * the whole company — so notify.js sanitises it against an allowlist before it is embedded.
 * Nothing about it being generated by this component makes it safe on the way back in.
 */

/* A fixed palette rather than a full picker. These go into an email that will be read on a
   phone in daylight and possibly printed in greyscale, so the useful choice is a handful of
   colours that survive both — not sixteen million that mostly do not. Kept in step with the
   COLOUR allowlist in server/src/repos/notify.js, which accepts any hex or rgb(). */
const TEXT_COLOURS = [
  ['#1d1d1f', 'Default'],
  ['#b00020', 'Red'],
  ['#b25000', 'Orange'],
  ['#1a7f37', 'Green'],
  ['#1f4e79', 'Blue'],
  ['#6b21a8', 'Purple'],
]
const HIGHLIGHTS = [
  ['#fff3a3', 'Yellow'],
  ['#ffd6d6', 'Red'],
  ['#d7f5dd', 'Green'],
  ['#dbeafe', 'Blue'],
  ['#eee1fb', 'Purple'],
  ['', 'None'],
]

export default function RichText({ value, onChange, placeholder, autoFocus = false }) {
  const hostRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const [palette, setPalette] = useState(null) // 'text' | 'mark' | null

  // The editor owns its own DOM once it is up, so `value` seeds it and is never written back
  // in — assigning innerHTML on every keystroke would move the caret to the start of the box
  // on each character typed.
  const seedRef = useRef(value)

  useEffect(() => {
    let cancelled = false
    const host = hostRef.current
    if (!host) return undefined

    import('pell').then(({ init }) => {
      if (cancelled || !hostRef.current) return
      const editor = init({
        element: host,
        // <p> per block rather than pell's default <div>: paragraphs are what the email's
        // own markup uses and what mail clients space correctly.
        defaultParagraphSeparator: 'p',
        onChange: (html) => onChangeRef.current(html),
        classes: {
          actionbar: 'rt-bar',
          button: 'rt-btn',
          content: 'rt-content',
          selected: 'rt-btn-on',
        },
        actions: [
          'bold',
          'italic',
          'underline',
          { name: 'olist', icon: '1.', title: 'Numbered list' },
          { name: 'ulist', icon: '&bull;', title: 'Bulleted list' },
          {
            name: 'link',
            icon: '&#128279;',
            title: 'Link',
            result: () => {
              const url = window.prompt('Link to where?')
              if (url) document.execCommand('createLink', false, url)
            },
          },
        ],
      })
      const content = editor.querySelector('.rt-content')
      if (content) {
        if (seedRef.current) content.innerHTML = seedRef.current
        if (placeholder) content.setAttribute('data-placeholder', placeholder)
        if (autoFocus) content.focus()
      }
    })

    return () => {
      cancelled = true
      // pell appends into the host; emptying it is the whole teardown, since it registers
      // nothing outside the element it was given.
      if (host) host.innerHTML = ''
    }
    // Deliberately mounted once. Re-running this would rebuild the editor mid-sentence.
  }, [placeholder, autoFocus])

  /**
   * Colour is applied through execCommand like every other control here, but its buttons
   * live outside the contenteditable — and clicking outside a contenteditable destroys the
   * selection before any handler runs, so there would be nothing left to colour.
   * Suppressing mousedown's default is what keeps the caret where it is: focus never
   * leaves, so the range is still there when the click lands.
   */
  const apply = (kind, colour) => {
    // Produces <span style="color:…"> instead of the legacy <font color>. The span is what
    // the server's allowlist expects, and what mail clients render most predictably.
    document.execCommand('styleWithCSS', false, true)
    if (kind === 'text') {
      document.execCommand('foreColor', false, colour || '#1d1d1f')
    } else if (colour) {
      document.execCommand('hiliteColor', false, colour)
    } else {
      // "None" — hiliteColor has no clearing value, and transparent is what actually removes
      // the mark rather than painting white over whatever is behind it.
      document.execCommand('hiliteColor', false, 'transparent')
    }
    document.execCommand('styleWithCSS', false, false)
    setPalette(null)
    const content = hostRef.current?.querySelector('.rt-content')
    if (content) onChangeRef.current(content.innerHTML)
  }

  const swatches = palette === 'text' ? TEXT_COLOURS : HIGHLIGHTS

  return (
    <div className="rt">
      {/* pell builds its toolbar and content into this node and owns its children entirely.
          The colour controls cannot go inside that toolbar — a pell action is a button with
          an icon string, with nowhere to hang a popover — so they are laid over the right
          end of it instead, which puts them in the same row without pell knowing. */}
      <div className="rt-host" ref={hostRef} />
      <div className="rt-colours">
        <button
          type="button"
          className={`rt-btn ${palette === 'text' ? 'rt-btn-on' : ''}`}
          title="Text colour"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setPalette((p) => (p === 'text' ? null : 'text'))}
        >
          <span className="rt-swatch-a">A</span>
        </button>
        <button
          type="button"
          className={`rt-btn ${palette === 'mark' ? 'rt-btn-on' : ''}`}
          title="Highlight"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setPalette((p) => (p === 'mark' ? null : 'mark'))}
        >
          <span className="rt-swatch-mark">A</span>
        </button>

        {palette && (
          <div className="rt-palette" role="menu">
            {swatches.map(([colour, label]) => (
              <button
                key={label}
                type="button"
                className={`rt-swatch ${colour ? '' : 'rt-swatch-none'}`}
                title={label}
                aria-label={label}
                style={colour ? { background: colour } : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => apply(palette, colour)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** The "there is no note yet" affordance, so the editor only appears once it is wanted. */
export function AddNoteButton({ onClick, children = 'Add a note' }) {
  return (
    <button type="button" className="btn ghost sm" onClick={onClick}>
      <Icon name="plus" size={13} /> {children}
    </button>
  )
}
